/**
 * Ключ localStorage: значение query-параметра `utm` (ключ пригласительной ссылки)
 * для поля `utm` в `POST .../register`. First-touch.
 */
const REGISTRATION_UTM_STORAGE_KEY = "tp_registration_utm";
const UTM_QUERY_PARAM = "utm";
/**
 * Ключ sessionStorage: гранулярная атрибуция (`click_id` + `utm_*`), прочитанная
 * из URL first-touch. Живёт в пределах вкладки, чтобы пережить переход на
 * регистрацию/логин (там query-параметров уже нет) и уйти в auth-запросы.
 *
 * Контракт бэка: если пришёл `click_id` — атрибуция берётся с записи клика
 * (гранулярные `utm_*` уже там). Если `click_id` нет, но есть хоть один `utm*` —
 * бэк сам создаёт direct-клик с этими UTM. Поэтому шлём всё, что поймали.
 */
const ATTRIBUTION_STORAGE_KEY = "tp_attribution_params";
/** Query-ключи гранулярной атрибуции (без yclid/ycid — те читаются отдельно). */
const ATTRIBUTION_QUERY_KEYS = [
    "click_id",
    "utm",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "ref",
];
function storageGet(key) {
    if (typeof window === "undefined") {
        return null;
    }
    try {
        const fromLocal = window.localStorage.getItem(key);
        if (fromLocal != null && fromLocal !== "") {
            return fromLocal;
        }
    }
    catch {
        /* private mode / disabled */
    }
    try {
        return window.sessionStorage.getItem(key);
    }
    catch {
        return null;
    }
}
function storageSet(key, value) {
    if (typeof window === "undefined") {
        return;
    }
    try {
        window.localStorage.setItem(key, value);
        return;
    }
    catch {
        /* quota / private mode */
    }
    try {
        window.sessionStorage.setItem(key, value);
    }
    catch {
        /* */
    }
}
/**
 * Строка query: в браузере надёжнее брать из `window.location`, чем из аргумента —
 * у `useLocation().search` на первом кадре иногда бывает `""`, хотя в адресной строке уже есть `?utm=...`.
 */
function resolveQueryString(search) {
    if (typeof window === "undefined") {
        return search ?? "";
    }
    const fromWindow = window.location.search;
    if (fromWindow && fromWindow.length > 0) {
        return fromWindow;
    }
    return search ?? "";
}
/** Раньше в storage попадал JSON `{"utm":"german"}` — для API нужна только строка `german`. */
function normalizeStoredUtm(raw) {
    const trimmed = raw.trim();
    if (!trimmed) {
        return null;
    }
    if (trimmed.startsWith("{")) {
        try {
            const parsed = JSON.parse(trimmed);
            const fromUtm = parsed.utm;
            if (typeof fromUtm === "string" && fromUtm.trim()) {
                return fromUtm.trim();
            }
        }
        catch {
            /* не JSON */
        }
    }
    return trimmed;
}
/** Читает уже сохранённую гранулярную атрибуцию из sessionStorage. */
function readStoredAttribution() {
    if (typeof window === "undefined") {
        return {};
    }
    try {
        const raw = window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
        if (!raw) {
            return {};
        }
        const parsed = JSON.parse(raw);
        const out = {};
        for (const key of ATTRIBUTION_QUERY_KEYS) {
            const v = parsed[key];
            if (typeof v === "string" && v.trim()) {
                out[key] = v.trim();
            }
        }
        return out;
    }
    catch {
        return {};
    }
}
/**
 * Захват гранулярной атрибуции (`click_id` + `utm_*`) из URL в sessionStorage.
 *
 * - Если в URL нет меток — ничего не трогаем (внутренняя навигация без query
 *   не должна затирать уже сохранённое).
 * - Если пришёл новый `click_id` (другой, чем в storage) — перезаписываем:
 *   иначе повторный клик по VK-объявлению в той же вкладке молча теряется.
 * - Если `click_id` нет, а storage уже непустой — first-touch (не затираем).
 */
function captureAttributionParams(search) {
    const params = new URLSearchParams(resolveQueryString(search));
    const captured = {};
    for (const key of ATTRIBUTION_QUERY_KEYS) {
        const v = params.get(key)?.trim();
        if (v) {
            captured[key] = v;
        }
    }
    if (Object.keys(captured).length === 0) {
        return;
    }
    const existing = readStoredAttribution();
    const incomingClickId = captured.click_id;
    const existingClickId = existing.click_id;
    const hasExisting = Object.keys(existing).length > 0;
    const isNewClick = !!incomingClickId && incomingClickId !== existingClickId;
    if (hasExisting && !isNewClick) {
        return;
    }
    try {
        window.sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(captured));
    }
    catch {
        /* private mode / quota — атрибуция этой загрузки не переживёт навигацию */
    }
}
/**
 * Сохраняет атрибуцию first-touch: ключ пригласительной ссылки из `?utm=...`
 * (для поля `utm` в регистрации) и гранулярную атрибуцию (`click_id` + `utm_*`).
 *
 * @param search — как `location.search` из React Router; если пустой, используется `window.location.search`.
 */
export function captureAttributionUtmFromLocation(search) {
    if (typeof window === "undefined") {
        return;
    }
    captureAttributionParams(search);
    if (storageGet(REGISTRATION_UTM_STORAGE_KEY)) {
        return;
    }
    const params = new URLSearchParams(resolveQueryString(search));
    const utmParam = params.get(UTM_QUERY_PARAM);
    if (!utmParam?.trim()) {
        return;
    }
    storageSet(REGISTRATION_UTM_STORAGE_KEY, utmParam.trim());
}
/**
 * Атрибуция для тела/URL auth-запросов: `click_id` + гранулярные `utm_*`
 * (first-touch, из sessionStorage). Поле `utm` берём из долгоживущего
 * localStorage-хранилища ({@link getStoredRegistrationUtm}) с fallback на
 * значение из sessionStorage-набора. Возвращаются только непустые поля.
 *
 * yclid/ycid здесь НЕ включены — их читают отдельно (cookie + ym getClientID).
 */
export function getAttributionForAuth() {
    const stored = readStoredAttribution();
    const result = { ...stored };
    const registrationUtm = getStoredRegistrationUtm();
    if (registrationUtm) {
        result.utm = registrationUtm;
    }
    return result;
}
/**
 * Значение для поля `utm` в теле регистрации (например `german`), не JSON.
 */
export function getStoredRegistrationUtm() {
    const raw = storageGet(REGISTRATION_UTM_STORAGE_KEY);
    if (!raw) {
        return null;
    }
    return normalizeStoredUtm(raw);
}
